import React, { useRef, useState, useEffect, ReactNode } from 'react';

export function LazyRenderWrapper({ content, defaultHeight = 150 }: { content: ReactNode; defaultHeight?: number }) {
  const [isIntersecting, setIsIntersecting] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const lastContentRef = useRef<ReactNode>(null);

  // detect when component is in viewport
  useEffect(() => {
    const observer = new window.IntersectionObserver(
      ([entry]) => {
        setIsIntersecting(entry.isIntersecting);
      },
      { threshold: 0.1 },
    );
    if (wrapperRef.current) observer.observe(wrapperRef.current);

    return () => observer.disconnect();
  }, []);

  // detect scrolling
  useEffect(() => {
    const sidePaneEl = wrapperRef.current?.closest('.SidePane');
    if (!sidePaneEl) return () => {};

    let scrollTimerId: number | undefined;

    const handleWheel = () => {
      setIsScrolling(true);

      // set timer for scroll end, since 'scrollend' is not reliable
      if (scrollTimerId) window.clearTimeout(scrollTimerId);
      scrollTimerId = window.setTimeout(() => setIsScrolling(false), 500);
    };

    // we use 'wheel' event to detect scroll start
    // 'scroll' event isn't reliable since it fires when height changes due to layout shifts
    sidePaneEl.addEventListener('wheel', handleWheel, { passive: true });
    return () => {
      sidePaneEl.removeEventListener('wheel', handleWheel);
      if (scrollTimerId) window.clearTimeout(scrollTimerId);
    };
  }, []);

  // update lastContent when content in viewport or scrolling
  if (isIntersecting || isScrolling) {
    lastContentRef.current = content;
  }

  // if intersecting or scrolling, render content
  // else render last content (so react doesn't re-render content out of view)
  // fallback to placeholder div with default height
  return (
    <div ref={wrapperRef}>
      {isIntersecting || isScrolling ? (
        content
      ) : lastContentRef.current ? (
        lastContentRef.current
      ) : (
        <div style={{ height: `${defaultHeight}px` }} />
      )}
    </div>
  );
}
